import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { ColumnsType } from 'antd/es/table';
import { PrettyCurrency, Typography } from 'components';
import { Invoice, InvoicePage } from 'gql/graphql';
import { ChevronDownIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { TGenericError } from 'types/TGenericError';
import { cn } from 'utils/cn';
import { dateShort } from 'utils/time/dateShort';
import PoCodeInvoices from '../_components/PoCodeInvoices';

enum InvoicesKeysEnum {
  Id = 'id',
  PurchaseOrderCode = 'purchase_order_code',
  Date = 'date',
  Amount = 'amount',
  Actions = 'actions'
}

interface IUseTableInvoicesIn {
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<InvoicePage, TGenericError>>;
  handleSelectInvoice: (id: string) => () => void;
}

interface IUseTableInvoicesOut {
  columns: ColumnsType<Invoice>;
}

export default function useTableInvoices({
  refetch,
  handleSelectInvoice
}: IUseTableInvoicesIn): IUseTableInvoicesOut {
  const t = useTranslations();

  const columns: ColumnsType<Invoice> = useMemo(
    () => [
      {
        title: t(`invoices.table.${InvoicesKeysEnum.Id}` as any),
        dataIndex: InvoicesKeysEnum.Id,
        key: InvoicesKeysEnum.Id,
        render: (_, invoices) => (
          <Typography
            variant="regular4"
            className={cn(
              'cursor-pointer',
              invoices.id ? 'text-tertiary-100' : 'text-tertiary-200'
            )}
          >
            {invoices.id ? `#${invoices.id}` : t('billing.invoice_items.not_applicable')}
          </Typography>
        )
      },
      {
        title: t(`invoices.table.${InvoicesKeysEnum.PurchaseOrderCode}` as any),
        dataIndex: InvoicesKeysEnum.PurchaseOrderCode,
        key: InvoicesKeysEnum.PurchaseOrderCode,
        render: (_, invoices) => (
          <PoCodeInvoices
            poCode={invoices.poCode}
            paid={invoices.paid}
            id={invoices.id}
            refetch={refetch}
          />
        )
      },
      {
        title: t(`invoices.table.${InvoicesKeysEnum.Date}` as any),
        dataIndex: InvoicesKeysEnum.Date,
        key: InvoicesKeysEnum.Date,
        render: (_, invoices) => (
          <Typography variant="regular4" className="cursor-pointer select-none  text-tertiary-100">
            {dateShort(invoices.issuedAt)}
          </Typography>
        )
      },
      {
        title: t(`invoices.table.${InvoicesKeysEnum.Amount}` as any),
        dataIndex: InvoicesKeysEnum.Amount,
        key: InvoicesKeysEnum.Amount,
        render: (_, invoices) => (
          <Typography
            variant={invoices.paid ? 'regular4' : 'medium4'}
            className={invoices.paid ? 'text-tertiary-100' : 'text-secondary-red'}
          >
            <PrettyCurrency
              className="!font-normal"
              amount={invoices.amount}
              currency={invoices.currency}
            />
          </Typography>
        )
      },
      {
        title: '',
        dataIndex: InvoicesKeysEnum.Actions,
        key: InvoicesKeysEnum.Actions,
        render: (_, invoices) => (
          <ChevronDownIcon
            className="-rotate-90 scale-125 cursor-pointer !text-primary-blue-100"
            onClick={handleSelectInvoice(invoices.id)}
          />
        )
      }
    ],
    [handleSelectInvoice, refetch, t]
  );

  return {
    columns
  };
}
